import { anyRarity } from './content.js';
import { MEADOW_DRAGON_GREETINGS, MEADOW_DRAGON_ITEMS, MEADOW_DRAGON_KEEPERS, MEADOW_DRAGON_NPCS, MEADOW_DRAGON_QUESTS, } from './meadow-dragon.js';
const CAPTAIN = { mob: 'captain', biome: 'meadow' };
const HERMIT = { mob: 'hermit', biome: 'meadow' };
const LOOKOUT = { mob: 'lookout', biome: 'meadow' };
const RANGER = { mob: 'ranger', biome: 'forest' };
const MERCHANT = { mob: 'merchant', biome: 'meadow' };
const STABLEMASTER = { mob: 'stablemaster', biome: 'meadow' };
/**
 * The meadow's chain: from the capital's square to the Great Scarecrow's
 * field, by the smith, the alchemist, the watchtower and the Circle of the
 * Elders, then west to Sylvebois. A little under half the experience of the band
 * (`quests.test.ts` holds the share), so a kill of the meadow pays less
 * than it did before the chain (ADR 0047).
 */
const QUESTS = [
    {
        id: 'meadow-portal',
        name: 'L’arche de Havrebourg',
        biome: 'meadow',
        level: 1,
        giver: CAPTAIN,
        turnIn: CAPTAIN,
        offer: 'Alors c’est toi, la recrue de Givrecoeur ? Il m’en envoie une par saison, et elles repartent toutes en courant au bout d’une semaine. Commence par toucher l’arche bleue, au milieu de la place. Si tu tombes au combat, c’est là que tu te relèveras. Ça m’évitera d’aller te chercher dans un fossé.',
        summary: 'Cliquer sur le portail au centre de la place de Havrebourg, puis revenir au capitaine.',
        pending: 'L’arche est au milieu de la place. Elle est grande et bleue, tu ne peux pas la rater.',
        complete: 'Bon, au moins tu sais où revenir. C’est déjà mieux que la dernière recrue.',
        objectives: [{ kind: 'portal', label: 'Enregistrer le portail', portal: 'meadow' }],
        reward: { xp: 10, gold: 2 },
    },
    {
        id: 'meadow-welcome',
        name: 'Bienvenue à Havrebourg',
        biome: 'meadow',
        level: 1,
        requires: ['meadow-portal'],
        giver: CAPTAIN,
        turnIn: CAPTAIN,
        offer: 'Avant de te donner du travail, va te présenter au forgeron, à l’alchimiste et au marchand. Ils te feront crédit si tu reviens vivant, alors autant qu’ils connaissent ta tête.',
        summary: 'Saluer le forgeron, l’alchimiste et le marchand de Havrebourg, puis revenir au capitaine.',
        pending: 'La forge est à l’ouest de la place. L’alchimiste au sud-est, tu le sentiras avant de le voir. Et le marchand à l’est, tu l’entendras avant de le voir.',
        complete: 'Le marchand t’a parlé de sa sœur ? Il en parle à tout le monde. Bon, passons aux affaires : j’ai un problème de rats.',
        objectives: [
            {
                kind: 'talk',
                label: 'Saluer le forgeron',
                npc: { mob: 'smith', biome: 'meadow' },
                line: 'Fais voir ton épée. Hum… du travail de camp. Ça coupe, c’est déjà ça. Reviens avec du silex et je t’en ferai quelque chose de correct.',
            },
            {
                kind: 'talk',
                label: 'Saluer l’alchimiste',
                npc: { mob: 'alchemist', biome: 'meadow' },
                line: 'Tu sens le rat ! Non non, c’est un compliment. Les queues de rat, je les fais bouillir trois heures et ça donne une potion qui remet sur pied. Rapporte-moi ce que laissent les bêtes, je t’en ferai des potions.',
            },
            {
                kind: 'talk',
                label: 'Saluer le marchand',
                npc: MERCHANT,
                line: 'Une nouvelle tête ! Tu viens du camp du vieux, hein ? Ma sœur dit qu’il mange de la neige pour se rappeler sa montagne. Bref ! Tout ce que tu chasses, je te le rachète. Enfin, presque tout. Repasse me voir, j’ai une petite affaire pour toi.',
            },
        ],
        reward: { xp: 25, gold: 5 },
    },
    {
        id: 'meadow-rats',
        name: 'Les nuisibles des champs',
        biome: 'meadow',
        level: 1,
        requires: ['meadow-welcome'],
        giver: CAPTAIN,
        turnIn: CAPTAIN,
        offer: 'Trois fermiers sont venus se plaindre ce matin. Pour des rats ! Les rats ont envahi les champs autour de la ville. Je paie deux pièces de cuivre la queue. Ramène-m’en cinq, et qu’on n’en parle plus jusqu’à la moisson.',
        summary: 'Chasser les rats autour de Havrebourg et rapporter leurs queues au capitaine.',
        pending: 'Les rats sont dans les champs, tout autour des murs. Rapporte-moi cinq queues, je ne paie pas sur parole.',
        complete: 'Cinq queues, le compte est bon. C’est rare, crois-moi. L’alchimiste me devait des potions, prends-les, je ne vais pas les boire moi-même.',
        objectives: [
            { kind: 'kill', label: 'Rats chassés', mobs: ['rat'], count: 10 },
            { kind: 'collect', label: 'Queues de rat', item: 'rat-tail', count: 5 },
        ],
        reward: { xp: 40, gold: 8, items: [{ templateId: 'potion-small', quantity: 3 }] },
    },
    {
        id: 'meadow-market',
        name: 'Le chapeau de paille',
        biome: 'meadow',
        level: 1,
        requires: ['meadow-welcome'],
        giver: MERCHANT,
        turnIn: MERCHANT,
        offer: 'Tu vas griller au soleil, mon ami ! Il te faut un Chapeau de paille, tressé par la femme du meunier. Achète-le et mets-le sur ta tête, et je te rembourse. Je veux juste qu’on le voie sur toi, c’est de la publicité !',
        summary: 'Acheter un Chapeau de paille chez le marchand de Havrebourg et le porter.',
        pending: 'Achète le chapeau, mets-le sur ta tête, et reviens me le montrer. Sur ta tête, pas dans ton sac !',
        complete: 'Magnifique ! Tout le monde va me demander où tu l’as trouvé. Tiens, ton or, je suis un homme de parole. Et la pelisse t’irait bien aussi, je dis ça comme ça.',
        objectives: [
            { kind: 'wear', label: 'Porter un Chapeau de paille', items: anyRarity('shepherd-hat') },
        ],
        reward: { xp: 15, gold: 3 },
    },
    {
        id: 'meadow-outfit',
        name: 'Habillé en berger',
        biome: 'meadow',
        level: 2,
        requires: ['meadow-market'],
        giver: MERCHANT,
        turnIn: MERCHANT,
        offer: 'Bon, le chapeau, c’est bien, mais les sangliers mordent fort. Mon cousin s’est fait mordre la cuisse l’an dernier, il boite encore. Achète une Pelisse de berger et un Bouclier d’osier, et je te fais un prix. Enfin, presque un prix.',
        summary: 'Acheter et porter la Pelisse de berger et le Bouclier d’osier.',
        pending: 'Quatre pièces d’or chacun. C’est donné ! Bon, c’est vendu, mais pas cher.',
        complete: 'Un vrai berger ! Il ne te manque plus que les moutons. Si tu trouves la même pelisse en mieux sur une bête, garde-la pour toi.',
        objectives: [
            { kind: 'wear', label: 'Porter une Pelisse de berger', items: anyRarity('shepherd-chest') },
            { kind: 'wear', label: 'Porter un Bouclier d’osier', items: anyRarity('shepherd-shield') },
        ],
        reward: { xp: 25, gold: 5 },
    },
    {
        id: 'meadow-smith',
        name: 'Le marteau et l’enclume',
        biome: 'meadow',
        level: 2,
        requires: ['meadow-welcome'],
        giver: { mob: 'smith', biome: 'meadow' },
        turnIn: { mob: 'smith', biome: 'meadow' },
        offer: 'Hald t’a déjà renforcé ça au camp, je vois. Il chauffe toujours trop, celui-là. Rapporte-moi du silex, les bêtes de la prairie en avalent, va savoir pourquoi, et je monterai une de tes pièces à deux crans.',
        summary: 'Faire porter une pièce d’équipement à +2 par le forgeron.',
        pending: 'Il me faut du silex et quelques pièces d’or. Je ne travaille pas gratuitement, moi.',
        complete: 'Voilà qui tiendra. Garde ces deux silex, j’en ai plein un tiroir.',
        objectives: [{ kind: 'upgrade', label: 'Une pièce portée à +2', level: 2 }],
        reward: { xp: 40, gold: 8, items: [{ templateId: 'flint', quantity: 2 }] },
    },
    {
        id: 'meadow-foxes',
        name: 'Le renard des fermes',
        biome: 'meadow',
        level: 2,
        requires: ['meadow-rats'],
        giver: CAPTAIN,
        turnIn: CAPTAIN,
        offer: 'Plus de rats, merci. Maintenant, ce sont les renards ! Ils ont mangé onze poules à Berthe cette semaine, et elle me les a toutes nommées, une par une. Va m’en chasser huit, que j’aie quelque chose à lui répondre.',
        summary: 'Chasser les renards qui rôdent dans la prairie.',
        pending: 'Les renards rôdent un peu plus loin que les rats, seuls ou à deux. Ils bougent tout le temps, sois rapide.',
        complete: 'Huit renards, parfait ! Si Berthe te demande les peaux, dis-lui que tu les as perdues. L’alchimiste a laissé ces potions pour toi. Je crois qu’il t’aime bien.',
        objectives: [{ kind: 'kill', label: 'Renards chassés', mobs: ['fox'], count: 8 }],
        reward: { xp: 50, gold: 10, items: [{ templateId: 'meadow-tonic', quantity: 3 }] },
    },
    {
        id: 'meadow-tower',
        name: 'La Tour de Guet',
        biome: 'meadow',
        level: 3,
        requires: ['meadow-foxes'],
        giver: CAPTAIN,
        turnIn: LOOKOUT,
        offer: 'Maëlle, la guetteuse de la tour à l’est, m’envoie un petit messager tous les soirs depuis six ans. Et là, rien depuis trois soirs. Va voir ce qui se passe. Et si elle te parle de lumières dans le ciel, ne l’encourage pas.',
        summary: 'Rejoindre la Tour de Guet et parler à la Guetteuse Maëlle.',
        pending: 'La tour est à l’est de la ville. C’est la seule chose plus haute qu’un arbre, tu la verras.',
        complete: 'Aldric s’inquiète ? C’est mignon. Le petit messager ne veut plus venir, voilà tout : des sangliers se sont installés au pied de ma tour. Et puis… j’ai vu autre chose, une nuit. Mais tu ne me croirais pas. Une autre fois.',
        objectives: [{ kind: 'reach', label: 'Atteindre la Tour de Guet', landmark: 'Tour de Guet' }],
        reward: { xp: 30, gold: 6 },
    },
    {
        id: 'meadow-boars',
        name: 'Les sangliers de la tour',
        biome: 'meadow',
        level: 3,
        requires: ['meadow-tower'],
        giver: LOOKOUT,
        turnIn: LOOKOUT,
        offer: 'Il y a dix sangliers, peut-être douze. Ils chargent tout ce qui approche de la colline. Chasse-les et rapporte-moi quatre défenses. Aldric ne croit que ce qu’il peut poser sur sa table.',
        summary: 'Repousser les sangliers de la prairie et rapporter leurs défenses à la Guetteuse.',
        pending: 'Les sangliers vivent loin des murs, vers la colline. Quand un sanglier fonce sur toi, écarte-toi !',
        complete: 'Je t’ai regardé faire depuis la tour. Onze sangliers ! J’en avais compté dix, il y en avait un qui se cachait. Prends ces guêtres, elles étaient à mon frère. Il est devenu boulanger, il n’en a plus besoin.',
        objectives: [
            { kind: 'kill', label: 'Sangliers repoussés', mobs: ['boar'], count: 10 },
            { kind: 'collect', label: 'Défenses de sanglier', item: 'boar-tusk', count: 4 },
        ],
        reward: { xp: 80, gold: 16, items: [{ templateId: 'shepherd-boots-rare', quantity: 1 }] },
    },
    {
        id: 'meadow-plague',
        name: 'Le Rat pesteux',
        biome: 'meadow',
        level: 4,
        requires: ['meadow-boars'],
        giver: LOOKOUT,
        turnIn: LOOKOUT,
        offer: 'Depuis ma tour, je vois un rat gros comme un chien, couvert de boutons. Beurk. Il change de terrier toutes les nuits. Prends cet appât qui pue, pose-le près d’un terrier de rats, et il sortira. Attention, il mord bien plus fort qu’un rat normal.',
        summary: 'Utiliser l’appât pestilentiel près d’un terrier de rats de la prairie, puis abattre le Rat pesteux qui en sort.',
        pending: 'Pose l’appât près d’un terrier de rats, hors du village. S’il t’échappe, recommence, il revient toujours à l’odeur.',
        complete: 'Bon débarras ! Il y a d’autres grosses bêtes comme lui dans la prairie. Leur nom est en couleur, tu les reconnaîtras. Elles cachent souvent de beaux trésors.',
        grants: [{ templateId: 'plague-bait', quantity: 1 }],
        objectives: [{ kind: 'kill', label: 'Rat pesteux abattu', mobs: ['plague-rat'], count: 1 }],
        reward: { xp: 70, gold: 14, items: [{ templateId: 'meadow-potion', quantity: 2 }] },
    },
    {
        id: 'meadow-circle',
        name: 'Le Cercle des Anciens',
        biome: 'meadow',
        level: 4,
        requires: ['meadow-boars'],
        giver: LOOKOUT,
        turnIn: HERMIT,
        offer: 'Il y a un champ, au sud-est, où plus rien ne pousse. Et la nuit, je vois quelque chose y marcher, avec des bras tout raides. L’ermite Aubépin, au nord, au milieu des grosses pierres, connaît ce champ. Va le voir de ma part.',
        summary: 'Se rendre au Cercle des Anciens, au nord, et parler à l’Ermite Aubépin.',
        pending: 'Au nord de la ville. Cherche des grosses pierres en cercle et un vieux monsieur qui leur parle.',
        complete: 'C’est Maëlle qui t’envoie ? Elle vient me voir une fois par an et elle ne s’assoit jamais. Toi, assieds-toi. Ce champ, oui… Il y a un vieil épouvantail planté dessus depuis toujours, et il s’est mis à marcher. Mes genoux me l’avaient dit, ils sentent tout.',
        objectives: [
            { kind: 'reach', label: 'Atteindre le Cercle des Anciens', landmark: 'Cercle des Anciens' },
        ],
        reward: { xp: 35, gold: 7 },
    },
    {
        id: 'meadow-camp',
        name: 'Le camp des maraudeurs',
        biome: 'meadow',
        level: 4,
        requires: ['meadow-circle'],
        giver: HERMIT,
        turnIn: HERMIT,
        offer: 'Tu veux affronter l’épouvantail comme ça, sans t’entraîner ? Montre-moi d’abord ce que tu vaux. Des sangliers gardent un coffre à l’est d’ici, et un autre coffre est caché en haut de la Butte aux Vents, au sud-ouest, on y monte par la grotte. Bats les gardiens de l’un des deux et ouvre le coffre. Et si tu vois des baies, rapporte-m’en !',
        summary: 'Vaincre les gardiens d’un camp de la prairie, à l’est du Cercle des Anciens ou au sommet de la Butte aux Vents, et ouvrir son coffre.',
        pending: 'Le coffre ne s’ouvre que quand tous les gardiens sont battus. Ils sont bêtes, mais pas à ce point.',
        complete: 'Pas de baies ? Tant pis. Prends ces gants, ils étaient déjà dans ce coffre la dernière fois que j’y suis allé, il y a trente ans. Personne ne fait jamais le ménage, ici.',
        objectives: [
            { kind: 'camp', label: 'Coffre d’un camp de la prairie', biome: 'meadow', count: 1 },
        ],
        reward: { xp: 90, gold: 18, items: [{ templateId: 'shepherd-gloves-rare', quantity: 1 }] },
    },
    {
        id: 'meadow-scarecrow',
        name: 'Le Champ Maudit',
        biome: 'meadow',
        level: 5,
        requires: ['meadow-camp'],
        giver: HERMIT,
        turnIn: CAPTAIN,
        offer: 'Le Grand Épouvantail se cache sous le champ, au sud-est. Il y a un escalier qui descend, ne me demande pas qui l’a creusé. Descends, bats-le, et va annoncer la nouvelle au capitaine Aldric. Lui, il a besoin qu’on lui dise les choses deux fois.',
        summary: 'Abattre le Grand Épouvantail au fond de son donjon, puis prévenir le capitaine.',
        pending: 'C’est au sud-est. Emporte des potions, je suis trop vieux pour descendre te chercher.',
        complete: 'Un épouvantail qui marche, sous un champ ? Je vais devoir écrire ça dans mon rapport, et personne ne va me croire. Tiens, prends cette pelisse, c’est un cadeau de la garde. Et Maëlle te cherche, à la tour. Cette nuit, pendant que tu étais sous terre, son petit messager est arrivé en courant. Elle ne veut parler qu’à toi.',
        objectives: [
            { kind: 'kill', label: 'Grand Épouvantail abattu', mobs: ['great-scarecrow'], count: 1 },
        ],
        reward: { xp: 140, gold: 30, items: [{ templateId: 'shepherd-chest-rare', quantity: 1 }] },
    },
    {
        id: 'meadow-stable',
        name: 'Le cheval promis',
        biome: 'meadow',
        level: 5,
        // The dragon's thread is the chain (ADR 0067): the horse waits on the recruit.
        requires: ['meadow-recruit'],
        giver: CAPTAIN,
        turnIn: STABLEMASTER,
        offer: 'Givrecoeur m’a parlé. Longtemps. Très longtemps. La Compagnie va t’envoyer loin, et à pied tu arriverais l’hiver prochain. Alors j’ai fait préparer un cheval pour toi, aux Écuries, à la sortie de la ville sur la route de Sylvebois. Gaspard te le donnera. Et ne me remercie pas, c’est la garde qui paie.',
        summary: 'Rejoindre les Écuries de Havrebourg et recevoir son cheval des mains du palefrenier.',
        pending: 'Les Écuries sont à la sortie de la ville, sur la route de Sylvebois. Suis l’odeur du crottin.',
        complete: 'C’est toi qui viens pour Noisette ? Le capitaine a dit « le meilleur cheval de l’enclos ». Le meilleur, c’est le mien, alors tu as le deuxième. Elle mord un peu, mais seulement à gauche. Et pas d’avoine après la tombée de la nuit, sinon elle fait des cauchemars.',
        objectives: [
            {
                kind: 'reach',
                label: 'Rejoindre les Écuries de Havrebourg',
                landmark: 'Écuries de Havrebourg',
            },
        ],
        reward: { xp: 20, gold: 4, items: [{ templateId: 'meadow-horse', quantity: 1 }] },
    },
    {
        id: 'meadow-west',
        name: 'La route de l’ouest',
        biome: 'meadow',
        level: 5,
        requires: ['meadow-stable'],
        giver: STABLEMASTER,
        turnIn: RANGER,
        offer: 'Tiens-toi droit, tu vas lui faire mal au dos ! Bon. À l’ouest, il y a la Forêt des Murmures, et au milieu, le village de Sylvebois. La garde-forestière, Ysolde, a besoin d’aide, d’après le capitaine. Et fais boire Noisette à chaque rivière !',
        summary: 'Suivre la route de l’ouest jusqu’à Sylvebois et parler à la Garde-forestière Ysolde.',
        pending: 'Sylvebois est plein ouest, au cœur de la forêt. Quand les arbres cachent le ciel, tu y es presque.',
        complete: 'C’est Aldric qui t’envoie ? À cheval, sous les arbres ? Descends, tu fais peur aux biches. Bon, on a besoin d’aide, même venue de la prairie.',
        objectives: [{ kind: 'reach', label: 'Atteindre Sylvebois', landmark: 'Sylvebois' }],
        reward: { xp: 40, gold: 8 },
    },
];
/**
 * The meadow's townsfolk of the quests stand in `mobs.ts` and the generator
 * places them (the chain came first); its bait is in `items.ts`, its
 * creatures' experience inline in the bestiary.
 */
export const MEADOW = {
    // The dragon's branch before the road west, so the chain still ends on its hand-over.
    quests: [...QUESTS.slice(0, -1), ...MEADOW_DRAGON_QUESTS, ...QUESTS.slice(-1)],
    npcs: MEADOW_DRAGON_NPCS,
    keepers: MEADOW_DRAGON_KEEPERS,
    items: MEADOW_DRAGON_ITEMS,
    xp: { fox: 8, boar: 12 },
    greetings: {
        ...MEADOW_DRAGON_GREETINGS,
        captain: 'Encore toi ? Tant mieux, j’ai toujours un problème à régler.',
        hermit: 'Tu n’as pas de baies sur toi, par hasard ? Non ? Dommage.',
        lookout: 'Rien à signaler au nord-est. Enfin, pour l’instant.',
        stablemaster: 'Pas d’avoine après la tombée de la nuit ! Je le dis à tout le monde, et personne n’écoute.',
    },
    greetingsBefore: {
        'meadow-farmer': {
            quest: 'meadow-scarecrow',
            line: 'Les renards m’ont encore pris deux poules cette semaine. Et l’autre nuit, j’ai vu une lumière rouge au nord, derrière les champs. Ma femme dit que c’est un feu de berger. Mais un feu de berger, ça n’éclaire pas les nuages, si ?',
        },
    },
};
//# sourceMappingURL=meadow.js.map